import React, { useState } from 'react';
import { Backdrop, Box, Button, CircularProgress, Fade, Modal, Typography } from '@mui/material';
import Add from '@mui/icons-material/Add';
import { AppButton } from '../../../../../../components/UI';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import { addremovecandidattoselection, getAllSelectionsWithoutCandidat } from '../../../../../../services/SelectionCandidatService';
import { updateObjectStateValue } from '../../../../../../utils/FunctionHelpers';
import { commonGridSx, gridLocaleText, makeGridToolbar } from '../../../../../../utils/gridStyles';
import { emptyRowSelection, getSelectedRowIds } from '../../../../../../utils/gridSelection';
import { DataGrid } from '../../../../../../components/SortableDataGrid/SortableDataGrid';

const CustomToolbar = makeGridToolbar('Sélections');

export default function CandidatSelectionsList({ loadSelectionsData, snackbarShowMessage, selectionsCandidat }) {
  const [modalState, setModalState] = useState({ open: false });
  const { idCandidat } = useParams();
  const [selectionsData, setSelectionsData] = useState({ isLoading: false, data: [] });
  const [rowSelectionModel, setRowSelectionModel] = useState(emptyRowSelection);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleOpen = () => setModalState({ open: true });
  const handleClose = () => { if (isSubmitting) return; setModalState({ open: false }); };

  const loadSelections = () => {
    updateObjectStateValue(setSelectionsData, { isLoading: true, data: [] });
    getAllSelectionsWithoutCandidat(idCandidat)
      .then(({ data }) => {
        updateObjectStateValue(setSelectionsData, { isLoading: false, data: data.selections });
        // Pre-select rows that candidate is already in
        const preSelected = [];
        (selectionsCandidat || []).forEach((sel) => {
          const found = (data.selections || []).findIndex((row) => row.id === sel.id);
          if (found !== -1) preSelected.push(data.selections[found].id);
        });
        setRowSelectionModel({ type: 'include', ids: new Set(preSelected) });
      })
      .catch(() => snackbarShowMessage('Erreur de chargement des sélections.', 'error', 4000))
      .finally(() => updateObjectStateValue(setSelectionsData, { isLoading: false }));
  };

  React.useEffect(() => {
    if (modalState.open) loadSelections();
  }, [modalState.open]);

  const handleSubmit = () => {
    snackbarShowMessage("Ajout du candidats dans les sélections...", "warning");
    setIsSubmitting(true);
    addremovecandidattoselection({ selectedSelections: getSelectedRowIds(rowSelectionModel, rows) }, idCandidat)
      .then(() => {
        snackbarShowMessage("Candidat a été ajouté dans les sélections.");
        setRowSelectionModel(emptyRowSelection());
        loadSelectionsData();
        loadSelections();
      })
      .catch(({ response }) => snackbarShowMessage(response?.data?.error?.detail, 'error', 4000))
      .finally(() => { setIsSubmitting(false); handleClose(); });
  };

  const columns = [
    {
      field: 'dateAjout', headerName: "DATE D'AJOUT", width: 130,
      renderCell: (p) => (
        <Typography sx={{ fontSize: '1rem', color: '#64748b' }}>
          {p.value?.date ? moment(p.value.date).format('DD/MM/YYYY') : '—'}
        </Typography>
      ),
    },
    {
      field: 'nom', headerName: 'NOM SÉLECTION', flex: 1, minWidth: 180,
      renderCell: (p) => (
        <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {p.value || '—'}
        </Typography>
      ),
    },
    {
      field: 'auteur', headerName: 'AUTEUR', width: 160,
      renderCell: (p) => <Typography sx={{ fontSize: '1rem', color: '#475569' }}>{p.value || '—'}</Typography>,
    },
  ];

  const rows = (selectionsData.data || []).map((s) => ({ ...s }));

  return (
    <>
      <AppButton
        variant="primary"
        size="small"
        onClick={handleOpen}
        startIcon={<Add fontSize="small" />}
        sx={{ mb: 1.5 }}
      >
        Ajouter une sélection
      </AppButton>
      <Modal
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        open={modalState.open}
        onClose={handleClose}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 500 }}
      >
        <Fade in={modalState.open}>
          <Box sx={{ background: '#fff', borderRadius: '20px', p: 3, width: '1000px', maxWidth: '95vw', display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#03407b' }}>LISTE DES SÉLECTIONS</Typography>
            <Box sx={{ borderRadius: 2, border: '1px solid #e2e8f0', height: '500px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <DataGrid
                rows={rows}
                columns={columns}
                getRowId={(row) => row.id}
                loading={selectionsData.isLoading}
                checkboxSelection
                rowSelectionModel={rowSelectionModel}
                onRowSelectionModelChange={(model) => setRowSelectionModel(model)}
                pageSizeOptions={[50, 100]}
                disableRowSelectionOnClick={false}
                showToolbar
                slots={{ toolbar: CustomToolbar }}
                slotProps={{ loadingOverlay: { variant: 'skeleton', noRowsVariant: 'skeleton' } }}
                localeText={gridLocaleText}
                sx={commonGridSx}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
              <Button variant="contained" onClick={handleSubmit} disabled={isSubmitting}
                sx={{ background: '#22c55e', color: '#fff', '&:hover': { background: '#16a34a' } }}>
                Sauvegarder
                {isSubmitting && <CircularProgress size={16} sx={{ ml: 1, color: '#fff' }} />}
              </Button>
              <Button variant="contained" onClick={handleClose} disabled={isSubmitting}
                sx={{ background: '#ef4444', color: '#fff', '&:hover': { background: '#dc2626' } }}>
                Annuler
              </Button>
            </Box>
          </Box>
        </Fade>
      </Modal>
    </>
  );
}
